import DocumentLayout from './Layout';
import { useEffect, useState, useRef } from 'react';
import RightSidebar from './Sidebar/RightSidebar';
import LeftSidebar from './Sidebar/LeftSidebar';
import { DocumentView } from './Sidebar/DocumentView';
import { DocumentModal } from '@/utils/enums';
import ModalAddFolder from './Modals/ModalAddFolder';
import ModalAddFiles from './Modals/ModalAddFiles';
import ModalEditFileName from './Modals/ModalEditFileName';
import ModalDeleteFile from './Modals/ModalDeleteFile';
import styles from '@/styles/documents.module.css';
import ModalMoveFile from './Modals/ModalMoveFile';
import { useAppContext } from '@/context';
import { Spin } from 'antd';
import Footer from '@/components/Footer';

export default function Documents({ setReloadData, isActive = false }: any) {
  const { user } = useAppContext();
  const [_, setCount] = useState<number>(0);
  const [documentView] = useState(
    () =>
      new DocumentView(user || {}, () => {
        setCount(prev => prev + 1);
        setReloadData((prev: boolean) => !prev);
      }),
  );
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [usersInDocuments, setUsersInDocuments] = useState<any>({});
  const [openModal, setOpenModal] = useState<DocumentModal | null>(null);
  const [nodeToDelete, setNodeToDelete] = useState<any>(null);
  const [nodeToEdit, setNodeToEdit] = useState<any>(null);
  const [nodeToMove, setNodeToMove] = useState<any>(null);
  const [isRefetching, setIsRefetching] = useState<boolean>(false);
  const previousOpenModalRef = useRef<DocumentModal | null>(null);
  const currentNodeWhenModalOpenedRef = useRef<any>(null);

  useEffect(() => {
    const initFolder = async () => {
      await documentView.init();
    };
    initFolder();
  }, [documentView]);

  useEffect(() => {
    if (!isActive) return;
    if (openModal) {
      previousOpenModalRef.current = openModal;
      currentNodeWhenModalOpenedRef.current = currentNode;
      return;
    }

    if (previousOpenModalRef.current !== null && openModal === null) {
      const previousModal = previousOpenModalRef.current;
      previousOpenModalRef.current = null;

      if (currentNodeWhenModalOpenedRef.current !== null) {
        const savedNode = currentNodeWhenModalOpenedRef.current;
        const findNodeInTree = (treeData: any[], nodeId: any): any => {
          for (const element of treeData) {
            if (element.id === nodeId) {
              return element;
            }
            if (element?.children) {
              const found = findNodeInTree(element.children, nodeId);
              if (found) return found;
            }
          }
          return null;
        };

        const buildParentChain = (node: any, treeData: any[]): any => {
          if (!node || !node.parent) return node;
          const parent = findNodeInTree(treeData, node.parent.id);
          if (parent) {
            return {
              ...node,
              parent: buildParentChain(parent, treeData),
            };
          }
          return node;
        };

        if (previousModal === DocumentModal.MOVE) {
          const updatedNode = findNodeInTree(
            documentView.treeData,
            savedNode?.id,
          );
          if (updatedNode) {
            const nodeWithParent = buildParentChain(
              updatedNode,
              documentView.treeData,
            );
            setCurrentNode(nodeWithParent || updatedNode);
            if (updatedNode.children && updatedNode.children.length > 0) {
              documentView.tableData = updatedNode.children;
            } else {
              documentView.tableData = documentView.treeData.filter(
                (item: any) => !item.parent,
              );
            }
            setCount(prev => prev + 1);
          } else {
            setCurrentNode(savedNode);
            if (savedNode.children && savedNode.children.length > 0) {
              documentView.tableData = savedNode.children;
            } else {
              const foundNode = findNodeInTree(
                documentView.treeData,
                savedNode?.id,
              );
              if (foundNode?.children && foundNode.children.length > 0) {
                documentView.tableData = foundNode.children;
              } else {
                documentView.tableData = documentView.treeData.filter(
                  (item: any) => !item.parent,
                );
              }
            }
            setCount(prev => prev + 1);
          }
        } else {
          const foundNode = findNodeInTree(
            documentView.treeData,
            savedNode?.id,
          );
          if (foundNode) {
            const nodeWithParent = buildParentChain(
              foundNode,
              documentView.treeData,
            );
            setCurrentNode(nodeWithParent || foundNode);
            if (foundNode.children && foundNode.children.length > 0) {
              documentView.tableData = foundNode.children;
            } else if (savedNode.children && savedNode.children.length > 0) {
              documentView.tableData = savedNode.children;
            }
          } else {
            setCurrentNode(savedNode);
            if (savedNode.children && savedNode.children.length > 0) {
              documentView.tableData = savedNode.children;
            }
          }
          setCount(prev => prev + 1);
        }
      } else {
        setCurrentNode(null);
        documentView.tableData = documentView.treeData.filter(
          (item: any) => !item.parent,
        );
        setCount(prev => prev + 1);
      }

      currentNodeWhenModalOpenedRef.current = null;
    }

    const refreshData = async () => {
      try {
        if (
          documentView.treeData.length === 0 &&
          documentView.tableData.length === 0
        ) {
          await documentView.init();
        } else {
          const params = currentNode?.id ? { folder_id: currentNode.id } : {};
          await documentView.refreshData(params);
          setCount(prev => prev + 1);
        }
      } catch (error) {
        // Silent fail
      }
    };

    const initialTimeout = setTimeout(refreshData, 1000);
    const intervalId = setInterval(refreshData, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [isActive, currentNode, documentView, openModal]);

  const onExpand = async (keys: any, info?: any) => {
    const previousKeys = expandedKeys;
    setExpandedKeys(keys);

    const newlyExpandedKey = keys.find(
      (key: string) => !previousKeys.includes(key),
    );

    if (newlyExpandedKey && info?.node && !info.node.isLeaf) {
      const node = info.node;

      const findNodeByKey = (treeData: any[], targetKey: string): any => {
        for (const item of treeData) {
          if (item.key === targetKey) return item;
          if (item.children) {
            const found = findNodeByKey(item.children, targetKey);
            if (found) return found;
          }
        }
        return null;
      };

      const foundNode =
        findNodeByKey(documentView.treeData, newlyExpandedKey) ||
        documentView.findNodeInTree(node.id, documentView.treeData);

      const hasChildrenLoaded =
        foundNode?.children && foundNode.children.length > 0;

      if (!hasChildrenLoaded && foundNode) {
        await documentView.onLoadData(foundNode);
        setCount(prev => prev + 1);
      }
    }
  };

  const buildParentChain = (targetNode: any, treeData: any[]): any => {
    const findNodeWithPath = (
      nodeId: string,
      nodes: any[],
      path: any[] = [],
    ): { node: any; path: any[] } | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return { node, path };
        }
        if (node.children) {
          const result = findNodeWithPath(nodeId, node.children, [
            ...path,
            node,
          ]);
          if (result) return result;
        }
      }
      return null;
    };

    const result = findNodeWithPath(targetNode.id, treeData);
    if (!result) return null;

    const { node, path } = result;

    if (path.length === 0) {
      return { ...node, parent: null };
    }

    const buildChain = (index: number): any => {
      if (index < 0) {
        return { ...node, parent: null };
      }
      const parentNode = { ...path[index] };
      parentNode.parent = index === 0 ? null : buildChain(index - 1);
      return parentNode;
    };

    const targetNodeWithParent = { ...node };
    targetNodeWithParent.parent = buildChain(path.length - 1);

    return targetNodeWithParent;
  };

  const onFolderNameClick = async (node: any) => {
    if (!node?.isLeaf) {
      const foundNode = documentView.findNodeInTree(
        node.id,
        documentView.treeData,
      );
      const hasChildrenLoaded =
        foundNode?.children && foundNode.children.length > 0;

      if (hasChildrenLoaded) {
        const nodeWithParent = buildParentChain(node, documentView.treeData);
        setCurrentNode(nodeWithParent || node);
        documentView.tableData = foundNode.children;
        setCount(prev => prev + 1);
      } else {
        await documentView.onLoadData(node);
        const updatedNode = documentView.findNodeInTree(
          node.id,
          documentView.treeData,
        );
        if (updatedNode?.children && updatedNode.children.length > 0) {
          const nodeWithParent = buildParentChain(
            updatedNode,
            documentView.treeData,
          );
          setCurrentNode(nodeWithParent || updatedNode);
          documentView.tableData = updatedNode.children;
          setCount(prev => prev + 1);
        } else {
          const nodeWithParent = buildParentChain(node, documentView.treeData);
          setCurrentNode(nodeWithParent || node);
        }
      }
    } else {
      const nodeWithParent = buildParentChain(node, documentView.treeData);
      setCurrentNode(nodeWithParent || node);
      documentView.tableData = [];
      setCount(prev => prev + 1);
    }
  };

  const onExpandTable = (node: any, isBack = false) => {
    if (isBack) {
      documentView.backToNode(node);
    } else {
      onFolderNameClick(node);
      if (!expandedKeys.includes(node.key)) {
        setExpandedKeys([...expandedKeys, node.key]);
      }
    }
  };

  return (
    <div className={styles?.documents} style={{ position: 'relative', flexDirection: 'column' }}>
      {isRefetching && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '4px',
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
            Updating folder list...
          </div>
        </div>
      )}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <DocumentLayout>
          <LeftSidebar
            navWidth={300}
            documentView={documentView}
            currentNode={currentNode}
            expandedKeys={expandedKeys}
            onExpand={onExpand}
            onFolderNameClick={onFolderNameClick}
            setOpenModal={setOpenModal}
            user={user}
          />
          <RightSidebar
            documentView={documentView}
            currentNode={currentNode}
            setCurrentNode={setCurrentNode}
            usersInDocuments={usersInDocuments}
            setUsersInDocuments={setUsersInDocuments}
            onExpand={onExpandTable}
            setOpenModal={setOpenModal}
            user={user}
            setNodeToDelete={setNodeToDelete}
            setNodeToEdit={setNodeToEdit}
            setNodeToMove={setNodeToMove}
          />
        </DocumentLayout>
      </div>
      <Footer />
      <ModalAddFolder
        node={currentNode}
        onLoadData={async (node, newNode) => {
          setIsRefetching(true);
          try {
            documentView.addNode(node, newNode);
            if (node?.id && !expandedKeys.includes(node.id)) {
              setExpandedKeys([...expandedKeys, node.id]);
            }
            if (currentNode && node && currentNode?.id === node?.id) {
              const updatedNode = documentView.findNodeInTree(
                node.id,
                documentView.treeData,
              );
              if (updatedNode?.children && updatedNode.children.length > 0) {
                documentView.tableData = updatedNode.children;
              } else {
                documentView.tableData = [...documentView.tableData];
              }
              setCount(prev => prev + 1);
            } else if (!node?.id && !currentNode) {
              documentView.tableData = documentView.treeData;
              setCount(prev => prev + 1);
            } else {
              setCount(prev => prev + 1);
            }
            await documentView.refetchFolderData(
              node?.id ? { folder_id: node.id } : {},
            );
          } catch (error) {
            // Silent fail
          } finally {
            setIsRefetching(false);
          }
        }}
        open={openModal == DocumentModal.ADD_FOLDER}
        onOk={() => setOpenModal(null)}
        onCancel={() => setOpenModal(null)}
      />
      <ModalAddFiles
        node={currentNode}
        onLoadData={async (node, newLeaves) => {
          setIsRefetching(true);
          try {
            documentView.addLeaves(node, newLeaves);
            if (node?.id && !expandedKeys.includes(node.id)) {
              setExpandedKeys([...expandedKeys, node.id]);
            }
            if (currentNode?.id === node?.id) {
              const updatedNode = documentView.findNodeInTree(
                node.id,
                documentView.treeData,
              );
              if (updatedNode?.children && updatedNode.children.length > 0) {
                documentView.tableData = updatedNode.children;
              } else {
                documentView.tableData = [...documentView.tableData];
              }
              setCount(prev => prev + 1);
            } else if (!node?.id && !currentNode) {
              documentView.tableData = documentView.treeData;
              setCount(prev => prev + 1);
            } else {
              setCount(prev => prev + 1);
            }
            await documentView.refetchFolderData(
              node?.id ? { folder_id: node.id } : {},
            );
          } catch (error) {
            // Silent fail
          } finally {
            setIsRefetching(false);
          }
        }}
        open={openModal == DocumentModal.ADD_FILE}
        onCancel={() => setOpenModal(null)}
      />
      <ModalEditFileName
        node={nodeToEdit}
        setNode={setCurrentNode}
        onLoadData={documentView.updateNodeName}
        open={openModal == DocumentModal.EDIT}
        onOk={() => {
          setOpenModal(null);
          setNodeToEdit(null);
        }}
        onCancel={() => {
          setOpenModal(null);
          setNodeToEdit(null);
        }}
      />
      <ModalDeleteFile
        node={nodeToDelete}
        setCurrentNode={setCurrentNode}
        onLoadData={documentView.removeNode}
        open={openModal == DocumentModal.DELETE}
        onCancel={() => {
          setOpenModal(null);
          setNodeToDelete(null);
        }}
      />
      <ModalMoveFile
        documentView={documentView}
        movingNode={nodeToMove}
        setMovingNode={setCurrentNode}
        usersInDocuments={usersInDocuments}
        setUsersInDocuments={setUsersInDocuments}
        open={openModal == DocumentModal.MOVE}
        onCancel={() => {
          setOpenModal(null);
          setNodeToMove(null);
        }}
      />
    </div>
  );
}
